import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Download, Maximize, Minimize, FileQuestion } from 'lucide-react';
import { getResourceDetails } from '../../apiServices/resourceApi';
import { getExtFromFileName, downloadFile } from '../../util/UtilFunctions';
import { validateFileTypeRendability } from '../../util/FileTypeSupportCheck';
import { sortItems } from '@/hooks/useSortedItems';
import { getRootFolder, getFolderById } from '@/api/folder';
import { getProjectFilesNormalized, getProjectById } from '@/api/project';
import type { NormalizedItem } from '@/components/itemList/types';
import { useNotification } from '@/hooks/useNotification';
import { useSession } from '@/hooks/useSession';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { selectItemsForParent, selectCacheAge, setItemsForParent, CACHE_MAX_AGE_MS } from '@/store/itemsSlice';
import { Spinner } from '@/components/ui/Spinner';
import ResourceViewer from './ResourceViewer';

// ── Constants ────────────────────────────────────────────────────────

// ── ResourceContent type (consumed by ResourceViewer) ────────────────

export interface ResourceContent {
  id: string;
  /** Always 'resource' — the breadcrumb bar branches on currentItem.itemtype */
  itemtype: string;
  type: string;
  name: string;
  signedUrl: string;
  mimeType: string;
  size: number;
  snapshotUrl?: string;
  previewImages?: string[];
  breadcrumb?: unknown[];
}

interface BreadcrumbEntry {
  id: string;
  title: string;
  itemtype: string;
}

/** Map a NormalizedItem to ResourceContent for ResourceViewer. */
function toResourceContent(item: NormalizedItem): ResourceContent {
  return {
    id: item.id,
    itemtype: 'resource',
    type: item.type || '',
    name: item.name || item.title,
    signedUrl: item.signedUrl || '',
    mimeType: item.mimeType || '',
    size: item.size ? Number(item.size) : 0,
    snapshotUrl: item.snapshotUrl,
    previewImages: item.previewImages?.map((p: unknown) =>
      typeof p === 'string' ? p : (p as { S?: string })?.S || '',
    ),
    breadcrumb: item.breadcrumb as unknown[],
  };
}

// ── Page Component ───────────────────────────────────────────────────

export default function ResourcePage() {
  const { id, parentId, viewType } = useParams<{ id: string; parentId: string; viewType: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const { init, updateIsQuerying, updateCurrentItem } = useSession();
  const currentUserGroup = useAppSelector((state) => state.workspace.currentUserGroup);

  const [items, setItems] = useState<ResourceContent[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | undefined>();
  const [fullscreen, setFullscreen] = useState(false);
  const [folderChain, setFolderChain] = useState<BreadcrumbEntry[] | null>(null);
  const cancelledRef = useRef(false);

  const parentType = location.pathname.split('/')[2]; // 'project' or 'folder'

  // ── Breadcrumb reconstruction ─────────────────────────────────────
  // The API doesn't populate `breadcrumb` on listed resources (only on the
  // parent item), so the breadcrumb bar only showed "Home > name". Rebuild
  // the chain from the parent's own breadcrumb + the parent itself, for both
  // folder and project parents.
  useEffect(() => {
    setFolderChain(null);
    if (!parentId || parentId === 'root' || !init) return;
    let cancelled = false;

    if (parentType === 'folder') {
      getFolderById(parentId)
        .then(({ folder }) => {
          if (cancelled || !folder?.id) return;
          const ancestors = Array.isArray(folder.breadcrumb)
            ? (folder.breadcrumb as BreadcrumbEntry[])
            : [];
          setFolderChain([
            ...ancestors,
            { id: folder.id, title: folder.title ?? '', itemtype: 'folder' },
          ]);
        })
        .catch(() => { /* breadcrumb stays Home-only on failure */ });
    } else if (parentType === 'project') {
      getProjectById(parentId)
        .then((res) => {
          if (cancelled) return;
          const item = res.response.Item as
            | (Record<string, { S?: string }> & { breadcrumb?: unknown })
            | undefined;
          const projectId = item?.id?.S || (item?.id as unknown as string);
          if (!projectId) return;
          const ancestors = Array.isArray(item?.breadcrumb)
            ? (item.breadcrumb as BreadcrumbEntry[])
            : [];
          setFolderChain([
            ...ancestors,
            {
              id: projectId,
              title: item?.title?.S || (item?.title as unknown as string) || '',
              itemtype: 'project',
            },
          ]);
        })
        .catch(() => { /* breadcrumb stays Home-only on failure */ });
    }

    return () => { cancelled = true; };
  }, [parentId, parentType, init]);

  // Sync the session's currentItem (drives the breadcrumb bar), attaching the
  // reconstructed folder chain when the resource has no breadcrumb of its own
  useEffect(() => {
    if (activeIndex === undefined) return;
    const content = items[activeIndex];
    if (!content) return;
    const hasOwnBreadcrumb = Array.isArray(content.breadcrumb) && content.breadcrumb.length > 0;
    updateCurrentItem(
      hasOwnBreadcrumb || !folderChain ? content : { ...content, breadcrumb: folderChain },
    );
  }, [activeIndex, items, folderChain]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Read from Redux cache ─────────────────────────────────────────

  const cachedItems = useAppSelector(selectItemsForParent(parentId));
  const cacheAge = useAppSelector(selectCacheAge(parentId));
  const isCacheStale = cacheAge !== undefined && cacheAge > CACHE_MAX_AGE_MS;

  // Parse sort params from URL (set by FileCard/FileRow links)
  const { sortIdx, ascending } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      sortIdx: parseInt(searchParams.get('sortBy') || '0', 10),
      ascending: searchParams.get('ascending') !== 'false',
    };
  }, [location.search]);

  // ── Apply items (shared by cache-hit and fetch paths) ─────────────

  const applyNormalizedItems = useCallback((normalized: NormalizedItem[]) => {
    // Filter to resources only (folder cache includes subfolders/projects)
    let resources = normalized.filter((i) => i.itemtype === 'resource');

    // Apply project viewType filter (matches server-side behavior)
    if (parentType === 'project' && viewType && viewType !== 'allfiles') {
      resources = resources.filter((i) => i.type === viewType);
    }

    const sorted = sortItems(resources, sortIdx, ascending);
    const asContent = sorted.map(toResourceContent);

    const idx = asContent.findIndex((i) => i.id === id);
    setItems(asContent);
    setActiveIndex(idx >= 0 ? idx : 0);
    // currentItem sync (incl. breadcrumb merge) happens in the effect above
  }, [id, parentType, viewType, sortIdx, ascending]);

  // ── Data loading (cache-first) ────────────────────────────────────

  const fetchAndCache = useCallback(async () => {
    if (!init || !currentUserGroup || !parentId) return;

    try {
      let normalized: NormalizedItem[];

      if (parentType === 'project') {
        normalized = await getProjectFilesNormalized(parentId);
      } else {
        const result = parentId === 'root'
          ? await getRootFolder()
          : await getFolderById(parentId);
        normalized = result.data as NormalizedItem[];
      }

      if (cancelledRef.current) return;

      // Store in Redux cache
      dispatch(setItemsForParent({ parentId, items: normalized }));
      applyNormalizedItems(normalized);
    } catch {
      // On error, show empty state (spinner will clear)
      if (!cancelledRef.current) {
        setItems([]);
        setActiveIndex(0);
      }
    }
  }, [parentId, parentType, init, currentUserGroup, dispatch, applyNormalizedItems]);

  useEffect(() => {
    cancelledRef.current = false;

    if (!init || !currentUserGroup) return;

    // Cache hit and not stale — use cached items directly
    if (cachedItems && cachedItems.length > 0 && !isCacheStale) {
      applyNormalizedItems(cachedItems);
    } else {
      // Cache miss or stale — fetch from API
      fetchAndCache();
    }

    return () => { cancelledRef.current = true; };
  }, [id, parentId, viewType, init, currentUserGroup, cachedItems, isCacheStale, sortIdx, ascending]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keyboard navigation ────────────────────────────────────────────

  const navigateToIndex = useCallback((newIdx: number) => {
    if (items.length === 0) return;
    const item = items[newIdx];
    const parts = location.pathname.split('/');
    parts[5] = item.id;
    navigate(parts.join('/'));
    setActiveIndex(newIdx);
  }, [items, location.pathname, navigate]);

  const navigateNext = useCallback(() => {
    if (activeIndex === undefined || items.length === 0) return;
    navigateToIndex(activeIndex >= items.length - 1 ? 0 : activeIndex + 1);
  }, [activeIndex, items.length, navigateToIndex]);

  const navigatePrev = useCallback(() => {
    if (activeIndex === undefined || items.length === 0) return;
    navigateToIndex(activeIndex <= 0 ? items.length - 1 : activeIndex - 1);
  }, [activeIndex, items.length, navigateToIndex]);

  useEffect(() => {
    function handleKeyUp(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') navigatePrev();
      if (e.key === 'ArrowRight') navigateNext();
    }
    window.addEventListener('keyup', handleKeyUp);
    return () => window.removeEventListener('keyup', handleKeyUp);
  }, [navigateNext, navigatePrev]);

  // ── Download ───────────────────────────────────────────────────────

  const handleDownload = useCallback(async () => {
    if (activeIndex === undefined) return;
    const content = items[activeIndex];
    try {
      updateIsQuerying(true);
      const res = await getResourceDetails(content.id);
      await downloadFile(content.name, (res as any).response.Item.signedUrl.S);
      notify({ type: 'success', message: 'Downloading file' });
    } catch {
      notify({ type: 'error', message: 'Could not download file' });
    } finally {
      updateIsQuerying(false);
    }
  }, [activeIndex, items, updateIsQuerying, notify]);

  // ── Derived state ──────────────────────────────────────────────────

  const content = activeIndex !== undefined ? items[activeIndex] : undefined;
  const fileExt = content ? getExtFromFileName(content.name) : '';
  const isRendable = content?.type ? validateFileTypeRendability(content.type, fileExt) : false;
  // Docs: only PDFs preview reliably. Other doc types (txt, css, …) were iframed
  // with the signed S3 URL, which is served as an attachment — the iframe showed
  // an empty box and auto-downloaded the file. They now get the fallback + Download button.
  const canPreview = isRendable && (content?.type !== 'doc' || fileExt === 'pdf');
  const isMedia = content?.type === 'video' || content?.type === 'audio';

  // ── Loading state ──────────────────────────────────────────────────

  if (activeIndex === undefined || !content) {
    return (
      <div className="flex h-[70vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div className="mx-auto flex h-[70vh] w-full max-w-7xl flex-col">
      {/* Resource viewer — takes all available space */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-background rounded-t-lg">
        {canPreview ? (
          <ResourceViewer
            content={content}
            fileExt={fileExt}
            fullscreen={fullscreen}
            onCloseFullscreen={() => setFullscreen(false)}
          />
        ) : (
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <FileQuestion className="h-12 w-12" />
            <p className="text-sm font-medium">.{fileExt} files can't be previewed</p>
            <button
              onClick={handleDownload}
              className="mt-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover"
            >
              Download file
            </button>
          </div>
        )}
      </div>

      {/* Toolbar — always pinned at bottom */}
      <div className="flex shrink-0 items-center justify-between border-t border-border bg-card px-4 py-2 rounded-b-lg">
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate(`/in/${parentType}/${parentId}`)}
            className="rounded p-2 hover:bg-secondary"
            title="Back"
          >
            <ArrowLeft className="h-5 w-5 text-muted-foreground" />
          </button>
          <button onClick={handleDownload} className="rounded p-2 hover:bg-secondary" title="Download">
            <Download className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={navigatePrev} className="rounded p-2 hover:bg-secondary" title="Previous">
            <ChevronLeft className="h-5 w-5 text-muted-foreground" />
          </button>
          <span className="min-w-12 text-center text-sm text-muted-foreground">
            {activeIndex + 1} / {items.length}
          </span>
          <button onClick={navigateNext} className="rounded p-2 hover:bg-secondary" title="Next">
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        <div className="flex items-center gap-3">
          {canPreview && !isMedia && (
            <button
              onClick={() => setFullscreen(!fullscreen)}
              className="rounded p-2 hover:bg-secondary"
              title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {fullscreen
                ? <Minimize className="h-5 w-5 text-muted-foreground" />
                : <Maximize className="h-5 w-5 text-muted-foreground" />}
            </button>
          )}
          <span className="max-w-[200px] truncate text-sm text-muted-foreground">{content.name}</span>
        </div>
      </div>
    </div>
  );
}
